<script setup lang="ts">
import { ref, watchEffect } from 'vue'
import { useUiStore } from '../stores/uiStore'
import { useUserStore } from '../stores/userStore'
import { checkNickname, updateNickname, updateFontSize, getPasskeyRegistrationOptions, verifyPasskeyRegistration, listPasskeys, deletePasskey, generateRecoveryCodes, getRecoveryCodeStatus } from '../services/authService'
import { startRegistration } from '@simplewebauthn/browser'
import ChangePasswordDialog from './ChangePasswordDialog.vue'
import RecoveryCodesDialog from './RecoveryCodesDialog.vue'

interface User {
  id: number
  email: string
  username: string
  nickname: string
}

interface Props {
  isOpen: boolean
  user: User | null
  snapshotCount: number
  bookmarkCount: number
  noteCount: number
}

interface Emits {
  (e: 'close'): void
  (e: 'update', user: Partial<User>): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const uiStore = useUiStore()
const userStore = useUserStore()

const isEditingNickname = ref(false)
const editingNickname = ref('')
const nicknameError = ref('')
const isSavingNickname = ref(false)
const showPasswordDialog = ref(false)

const startEditNickname = () => {
  if (!props.user) return
  editingNickname.value = props.user.nickname
  nicknameError.value = ''
  isEditingNickname.value = true
}

const cancelNicknameEdit = () => {
  isEditingNickname.value = false
  nicknameError.value = ''
}

const saveNickname = async () => {
  if (!props.user || !userStore.csrfToken) return
  nicknameError.value = ''

  const newNickname = editingNickname.value.trim()
  if (!newNickname) {
    nicknameError.value = '暱稱不可為空'
    return
  }

  if (newNickname === props.user.nickname) {
    isEditingNickname.value = false
    return
  }

  isSavingNickname.value = true

  const check = await checkNickname(newNickname)
  if (check.exists) {
    nicknameError.value = '此暱稱已存在'
    isSavingNickname.value = false
    return
  }

  const result = await updateNickname(props.user.id, newNickname)
  if (result.success && userStore.currentUser) {
    userStore.currentUser = { ...userStore.currentUser, nickname: newNickname }
    emit('update', { nickname: newNickname })
    isEditingNickname.value = false
  } else {
    nicknameError.value = result.error || '暱稱更新失敗'
  }

  isSavingNickname.value = false
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    saveNickname()
  } else if (e.key === 'Escape') {
    cancelNicknameEdit()
  }
}

const handleFontDecrease = async () => {
  if (!props.user || !userStore.csrfToken) return
  uiStore.decrease()
  await updateFontSize(props.user.id, uiStore.fontSize)
}

const handleFontIncrease = async () => {
  if (!props.user || !userStore.csrfToken) return
  uiStore.increase()
  await updateFontSize(props.user.id, uiStore.fontSize)
}

const handleFontReset = async () => {
  if (!props.user || !userStore.csrfToken) return
  uiStore.reset()
  await updateFontSize(props.user.id, uiStore.fontSize)
}

// Passkey 管理
const passkeys = ref<Array<{ id: string; name: string; createdAt: string; backupEligible: boolean; transports: string[] }>>([])
const isLoadingPasskeys = ref(false)
const isAddingPasskey = ref(false)
const passkeyError = ref('')

const getPasskeyIcon = (transports: string[]) => {
  if (transports.includes('internal')) return '💻'
  if (transports.includes('hybrid')) return '📱'
  return '🔑'
}

const getPasskeyDeviceType = (transports: string[]) => {
  if (transports.includes('internal')) return '此裝置'
  if (transports.includes('hybrid')) return '跨裝置'
  return 'Passkey'
}

const loadPasskeys = async () => {
  if (!userStore.csrfToken) return
  passkeyError.value = ''
  isLoadingPasskeys.value = true
  const result = await listPasskeys()
  if (result.success && result.data) {
    passkeys.value = result.data.credentials
  }
  isLoadingPasskeys.value = false
}

watchEffect(() => {
  if (props.isOpen) {
    loadPasskeys()
  }
})

const handleAddPasskey = async () => {
  if (!userStore.csrfToken) return
  passkeyError.value = ''
  isAddingPasskey.value = true

  try {
    const optionsResponse = await getPasskeyRegistrationOptions()
    if (!optionsResponse.success || !optionsResponse.data) {
      passkeyError.value = optionsResponse.error || '取得註冊選項失敗'
      isAddingPasskey.value = false
      return
    }

    const { options, challengeId } = optionsResponse.data

    const attestation = await startRegistration({
      optionsJSON: options,
    })

    const verifyResponse = await verifyPasskeyRegistration(challengeId, attestation)
    if (verifyResponse.success) {
      await loadPasskeys()
    } else {
      passkeyError.value = verifyResponse.error || 'Passkey 註冊失敗'
    }
  } catch (error: any) {
    console.error('Add passkey error:', error)
    const errorMsg = error.message || 'Passkey 註冊失敗'

    // 處理 WebAuthn 特定錯誤
    if (errorMsg.includes('previously registered') || errorMsg.includes('已註冊') || errorMsg.includes('NotAllowedError')) {
      passkeyError.value = '此 Passkey 已在本帳號註冊（可能因密碼應用程式同步）。建議：(1) 在不同裝置註冊、(2) 或在同一裝置使用不同的生物識別方式'
    } else if (errorMsg.includes('NotSupportedError')) {
      passkeyError.value = '您的裝置不支援 WebAuthn'
    } else if (errorMsg.includes('SecurityError')) {
      passkeyError.value = '安全性檢查失敗，請檢查網域配置'
    } else if (errorMsg.includes('invalid assertion')) {
      passkeyError.value = '驗證失敗，請重試'
    } else {
      passkeyError.value = errorMsg
    }
  } finally {
    isAddingPasskey.value = false
  }
}

const handleDeletePasskey = async (credentialId: string) => {
  if (!userStore.csrfToken) return
  if (!confirm('確定要刪除此 Passkey？')) return

  const result = await deletePasskey(credentialId)
  if (result.success) {
    await loadPasskeys()
  } else {
    passkeyError.value = result.error || 'Passkey 刪除失敗'
  }
}

// 恢復碼管理
const recoveryCodeStatus = ref<{ total: number; unused: number } | null>(null)
const isLoadingRecoveryCodes = ref(false)
const recoveryError = ref('')
const showRecoveryCodesDialog = ref(false)
const recoveryCodesDisplay = ref<string[]>([])

const loadRecoveryCodeStatus = async () => {
  if (!userStore.csrfToken) return
  recoveryError.value = ''
  isLoadingRecoveryCodes.value = true
  const result = await getRecoveryCodeStatus()
  if (result.success && result.data) {
    recoveryCodeStatus.value = result.data
  } else if (result.error) {
    recoveryError.value = result.error
  }
  isLoadingRecoveryCodes.value = false
}

const handleGenerateRecoveryCodes = async () => {
  if (!userStore.csrfToken) return

  const shouldGenerate = recoveryCodeStatus.value && recoveryCodeStatus.value.unused > 0
    ? confirm('重新生成將作廢所有舊碼，確定繼續？')
    : true

  if (!shouldGenerate) return

  recoveryError.value = ''
  isLoadingRecoveryCodes.value = true

  const result = await generateRecoveryCodes()
  if (result.success && result.data) {
    recoveryCodesDisplay.value = result.data.codes
    showRecoveryCodesDialog.value = true
    await loadRecoveryCodeStatus()
  } else {
    recoveryError.value = result.error || '生成恢復碼失敗'
  }

  isLoadingRecoveryCodes.value = false
}

watchEffect(() => {
  if (props.isOpen) {
    loadRecoveryCodeStatus()
  }
})
</script>

<template>
  <div v-if="isOpen" class="info-overlay">
    <div class="info-dialog" @click.stop>
      <div class="info-header">
        <h2>個人資訊</h2>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <div class="info-content">
        <div v-if="user" class="info-display">
          <div class="info-item">
            <span class="info-label">帳號:</span>
            <span class="info-value">{{ user.username }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">電子信箱:</span>
            <span class="info-value">{{ user.email }}</span>
          </div>

          <div class="info-item">
            <span class="info-label">暱稱:</span>
            <span
              v-if="!isEditingNickname"
              class="info-value editable-nickname"
              @dblclick="startEditNickname"
              title="雙擊編輯"
            >
              {{ user.nickname }}
            </span>
            <input
              v-if="isEditingNickname"
              v-model="editingNickname"
              type="text"
              class="form-input"
              :disabled="isSavingNickname"
              @keydown="handleKeydown"
              autofocus
            />
          </div>

          <div v-if="nicknameError" class="error-text">{{ nicknameError }}</div>

          <div class="info-actions">
            <button
              class="info-btn primary"
              @click="showPasswordDialog = true"
              :style="{ fontSize: uiStore.fontSize + 'px' }"
            >
              密碼變更
            </button>
            <button
              class="info-btn primary"
              @click="isEditingNickname ? saveNickname() : startEditNickname()"
              :disabled="isSavingNickname"
              :style="{ fontSize: uiStore.fontSize + 'px' }"
            >
              {{ isSavingNickname ? '儲存中...' : isEditingNickname ? '儲存暱稱' : '編輯暱稱' }}
            </button>
          </div>

          <div class="info-divider"></div>

          <div class="font-size-control">
            <span class="font-size-label">字型：</span>
            <div class="font-size-btns">
              <button class="font-size-btn" :disabled="!uiStore.canDecrease" @click="handleFontDecrease">A-</button>
              <span class="font-size-value">{{ uiStore.fontSize }}</span>
              <button class="font-size-btn" :disabled="!uiStore.canIncrease" @click="handleFontIncrease">A+</button>
            </div>
            <button class="reset-btn" :disabled="uiStore.isDefault" @click="handleFontReset" style="flex-shrink: 0">恢復預設值</button>
          </div>

          <div class="info-divider"></div>

          <!-- Passkey 管理 -->
          <div class="passkey-management">
            <h3 class="passkey-title">Passkey 快速登入 ({{ passkeys.length }})</h3>

            <div v-if="isLoadingPasskeys" class="loading-text">載入中...</div>
            <div v-else-if="passkeys.length === 0" class="empty-text">尚未設定 Passkey</div>
            <div v-else class="passkeys-list">
              <div v-for="passkey in passkeys" :key="passkey.id" class="passkey-item">
                <span class="passkey-icon">{{ getPasskeyIcon(passkey.transports) }}</span>
                <span class="passkey-name">{{ getPasskeyDeviceType(passkey.transports) }} {{ new Date(passkey.createdAt).toLocaleDateString('zh-TW') }}</span>
                <button
                  class="delete-btn"
                  @click="handleDeletePasskey(passkey.id)"
                >
                  刪除
                </button>
              </div>
            </div>

            <button
              class="add-passkey-btn"
              @click="handleAddPasskey"
              :disabled="isAddingPasskey"
              :style="{ fontSize: uiStore.fontSize + 'px' }"
            >
              {{ isAddingPasskey ? '驗證中...' : '+ 新增 Passkey' }}
            </button>

            <div class="passkey-hint">
              💡 建議優先在手機上添加 Passkey（掃描 QR 碼），讓登入時無需密碼。也可在此裝置添加備用。
              <span v-if="isAddingPasskey" class="passkey-hint-active">
                📱 選擇「使用手機或平板（掃 QR 碼）」體驗最佳的 Keyless 登入
              </span>
            </div>

            <div v-if="passkeyError" class="error-text">
              {{ passkeyError }}
            </div>
          </div>

          <div class="info-divider"></div>

          <!-- 恢復碼管理 -->
          <div class="recovery-code-management">
            <h3 class="recovery-title">恢復碼</h3>

            <div v-if="isLoadingRecoveryCodes" class="loading-text">載入中...</div>
            <div v-else-if="recoveryCodeStatus">
              <div class="recovery-status">
                <span class="recovery-status-text">
                  {{
                    recoveryCodeStatus.unused === 0
                      ? '狀態：尚未生成'
                      : `剩餘 ${recoveryCodeStatus.unused} / ${recoveryCodeStatus.total} 組可用`
                  }}
                </span>
                <button
                  class="recovery-btn"
                  @click="handleGenerateRecoveryCodes"
                  :disabled="isLoadingRecoveryCodes"
                  :style="{ fontSize: uiStore.fontSize + 'px' }"
                >
                  {{
                    recoveryCodeStatus.unused === 0
                      ? '生成恢復碼'
                      : '重新生成'
                  }}
                </button>
              </div>
              <div class="recovery-note">用於無法使用 Passkey 時的緊急登入</div>
            </div>

            <div v-if="recoveryError" class="error-text">{{ recoveryError }}</div>
          </div>

          <div class="info-divider"></div>

          <div class="info-stats">
            <div class="stat-item">
              <span class="stat-label">版面記錄</span>
              <span class="stat-value">{{ snapshotCount }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">書簽</span>
              <span class="stat-value">{{ bookmarkCount }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">筆記</span>
              <span class="stat-value">{{ noteCount }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ChangePasswordDialog
      :is-open="showPasswordDialog"
      :user-id="user?.id || 0"
      @close="showPasswordDialog = false"
    />

    <RecoveryCodesDialog
      :is-open="showRecoveryCodesDialog"
      :codes="recoveryCodesDisplay"
      @close="showRecoveryCodesDialog = false"
    />
  </div>
</template>

<style scoped>
.info-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 320;
}

.info-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: 90%;
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.info-header h2 {
  margin: 0;
  font-size: 18px;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #1f2937;
}

.info-content {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  max-height: calc(90vh - 60px);
}

.info-display {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  align-items: center;
}

.info-label {
  font-weight: 500;
  color: #6b7280;
  min-width: 80px;
}

.info-value {
  color: #1f2937;
  word-break: break-all;
}

.editable-nickname {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}

.editable-nickname:hover {
  background: #f3f4f6;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid #3b82f6;
  border-radius: 4px;
  font-size: 14px;
  width: auto;
  max-width: 200px;
}

.form-input:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: -8px;
}

.info-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.info-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 8px 0;
}

.info-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 6px;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #3b82f6;
}

.info-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  flex: 1;
}

.info-btn.primary {
  background: #3b82f6;
  color: white;
}

.info-btn.primary:hover:not(:disabled) {
  background: #2563eb;
}

.info-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.font-size-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.font-size-label {
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}

.font-size-btns {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px;
}

.font-size-btn {
  background: transparent;
  border: none;
  padding: 4px 6px;
  font-weight: 600;
  cursor: pointer;
  color: #333;
  font-size: 14px;
  transition: all 0.2s;
}

.font-size-btn:hover:not(:disabled) {
  background: #f3f4f6;
}

.font-size-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.font-size-value {
  min-width: 24px;
  text-align: center;
  font-weight: 500;
  color: #1f2937;
  font-size: 14px;
}

.reset-btn {
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  color: #6b7280;
  transition: all 0.2s;
  white-space: nowrap;
}

.reset-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.reset-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.passkey-management {
  margin: 8px 0;
}

.passkey-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  margin-top: 0;
}

.loading-text,
.empty-text {
  font-size: 12px;
  color: #9ca3af;
  padding: 8px 0;
  text-align: center;
}

.passkeys-list {
  margin-bottom: 8px;
}

.passkey-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  background: #f9fafb;
  border-radius: 4px;
  margin-bottom: 6px;
  font-size: 12px;
  gap: 6px;
}

.passkey-icon {
  font-size: 14px;
  flex-shrink: 0;
}

.passkey-name {
  flex: 1;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.passkey-hint {
  font-size: 11px;
  color: #6b7280;
  margin-top: 6px;
  margin-bottom: 6px;
  line-height: 1.4;
}

.passkey-hint-active {
  display: block;
  margin-top: 6px;
  color: #3b82f6;
  font-weight: 500;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.delete-btn {
  padding: 4px 8px;
  margin-left: 8px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 4px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  font-size: 11px;
}

.delete-btn:hover {
  background: #fee2e2;
  border-color: #ef4444;
  color: #ef4444;
}

.add-passkey-btn {
  width: 100%;
  padding: 8px;
  margin-top: 4px;
  border: 1px dashed #3b82f6;
  background: #eff6ff;
  color: #3b82f6;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  font-size: 12px;
}

.add-passkey-btn:hover:not(:disabled) {
  background: #dbeafe;
  border-color: #2563eb;
}

.add-passkey-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recovery-code-management {
  margin: 8px 0;
}

.recovery-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  margin-top: 0;
}

.recovery-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
}

.recovery-status-text {
  font-size: 12px;
  color: #1f2937;
  flex: 1;
}

.recovery-btn {
  padding: 6px 12px;
  border: 1px solid #3b82f6;
  background: #eff6ff;
  color: #3b82f6;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.recovery-btn:hover:not(:disabled) {
  background: #dbeafe;
  border-color: #2563eb;
  color: #2563eb;
}

.recovery-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recovery-note {
  font-size: 11px;
  color: #6b7280;
  margin-top: 6px;
  line-height: 1.4;
}
</style>
